import React, { useState, useEffect } from 'react';
import { Headline } from '../../types/Headline';
import { FileText, Hash, Eye, EyeOff } from 'lucide-react';

export interface HeadlineFormData {
  title: string;
  content: string;
  schoolId: string;
  active: boolean;
  displayOrder: number;
}

interface HeadlineFormProps {
  headline?: Headline | null;
  onSubmit: (data: HeadlineFormData) => Promise<void>;
  onCancel: () => void;
}

const HeadlineForm: React.FC<HeadlineFormProps> = ({ headline, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<HeadlineFormData>({
    title: '',
    content: '',
    schoolId: '',
    active: true,
    displayOrder: 1,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current user's school ID from localStorage
  const getCurrentSchoolId = (): string => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.schoolId || '';
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    return '';
  };

  useEffect(() => {
    const schoolId = getCurrentSchoolId();

    if (headline) {
      // Editing existing headline
      setFormData({
        title: headline.title,
        content: headline.content,
        schoolId: headline.schoolId,
        active: headline.active,
        displayOrder: headline.displayOrder,
      });
    } else {
      // Creating new headline
      setFormData(prev => ({
        ...prev,
        schoolId,
      }));
    }
  }, [headline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting headline:', error);
      setError('Failed to save headline. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof HeadlineFormData, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {headline ? 'Edit Headline' : 'Create New Headline'}
            </h2>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Display Order */}
            <div>
              <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 mb-2">
                <Hash className="inline h-4 w-4 mr-1" />
                Display Order
              </label>
              <input
                type="number"
                id="displayOrder"
                min="1"
                value={formData.displayOrder}
                onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value, 10) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Headlines will be displayed in ascending order (1, 2, 3...)
              </p>
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter headline title"
                required
              />
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                id="content"
                rows={4}
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter headline content"
                required
              />
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {formData.active ? (
                    <>
                      <Eye className="inline h-4 w-4 mr-1" />
                      Active (visible to users)
                    </>
                  ) : (
                    <>
                      <EyeOff className="inline h-4 w-4 mr-1" />
                      Inactive (hidden from users)
                    </>
                  )}
                </span>
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : headline ? 'Update Headline' : 'Create Headline'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HeadlineForm;
